import type { ContentAudience } from './audience';

export interface ResourceListGetter {
    get<T = unknown>(url: string): Promise<{ data: T }>;
}

// v2 list endpoints cap `limit` at 100, so lists are paged through.
const PAGE_SIZE = 100;
// Safety bound so an unexpected non-terminating response can't loop forever.
const MAX_PAGES = 100;

interface PaginatedEnvelope<T> {
    data?: T[];
    meta?: { totalPages?: number };
}

/** Pulls the items (and page count) out of a v2 list response, unwrapped or not. */
function extractPage<T>(responseData: T[] | PaginatedEnvelope<T>): {
    items: T[];
    totalPages?: number;
} {
    if (Array.isArray(responseData)) {
        return { items: responseData };
    }
    return { items: responseData?.data ?? [], totalPages: responseData?.meta?.totalPages };
}

export function resourceListPageUrl(
    path: string,
    page: number,
    audience?: ContentAudience
): string {
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    if (audience) params.set('audience', audience);
    return `${path}?${params.toString()}`;
}

/** Loads a full v2 resource list, paging to the last page (or first short page). */
export async function fetchAllResourcePages<T>(
    api: ResourceListGetter,
    path: string,
    audience?: ContentAudience
): Promise<T[]> {
    const all: T[] = [];
    let page = 1;
    let done = false;

    do {
        const response = await api.get<T[] | PaginatedEnvelope<T>>(
            resourceListPageUrl(path, page, audience)
        );
        const { items, totalPages } = extractPage<T>(response.data);
        all.push(...items);
        done = totalPages != null ? page >= totalPages : items.length < PAGE_SIZE;
        page += 1;
    } while (!done && page <= MAX_PAGES);

    return all;
}
